import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Real-time live online user count.
 * - Sends a heartbeat every 30s to mark this user as online.
 * - Returns the current online count (users active within last 90s).
 * - Subscribes to realtime changes for instant updates.
 * - NEVER returns a fake/cached value — if backend fails, count is 0.
 */
export function useLiveCount() {
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const safeSet = (value: unknown) => {
      if (cancelled) return;
      // Only accept finite integers from backend; otherwise show 0.
      const n = typeof value === "number" && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
      setCount(n);
    };

    const refresh = async () => {
      try {
        const { data, error } = await (supabase.rpc as any)("presence_heartbeat");
        if (cancelled) return;
        if (!error) {
          safeSet(data);
        } else {
          // Anonymous fallback: public count function
          const { data: c, error: e2 } = await (supabase.rpc as any)("get_online_count");
          if (e2) safeSet(0);
          else safeSet(c);
        }
      } catch {
        safeSet(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    refresh();
    tickRef.current = setInterval(refresh, 30000);

    // Realtime subscription — any change in presence triggers a recount
    const channel = supabase
      .channel("presence-live-count")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_presence" },
        async () => {
          const { data, error } = await (supabase.rpc as any)("get_online_count");
          if (error) safeSet(0);
          else safeSet(data);
        }
      )
      .subscribe();

    const handleVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const handleUnload = () => {
      try {
        (supabase.rpc as any)("presence_goodbye");
      } catch {}
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", handleUnload);

    return () => {
      cancelled = true;
      if (tickRef.current) clearInterval(tickRef.current);
      supabase.removeChannel(channel);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", handleUnload);
      handleUnload();
    };
  }, []);

  return { count, loading };
}
